import { useState, useEffect, useRef, useCallback } from 'react';
import { getChatConversations, getChatMessages, sendChatMessage, getUsers } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatApiError, getInitials } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { PaperPlaneRight, MagnifyingGlass, ChatCircleDots, Circle } from '@phosphor-icons/react';

export default function ChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await getChatConversations();
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to load conversations');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [convRes, usersRes] = await Promise.all([
          getChatConversations(),
          getUsers()
        ]);
        setConversations(convRes.data);
        setAllUsers(usersRes.data.filter(u => u.id !== user.id));
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user.id]);

  // Poll for new messages
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      fetchConversations();
      if (selectedPartner) {
        getChatMessages(selectedPartner.id).then(res => setMessages(res.data)).catch(() => {});
      }
    }, 5000);
    return () => clearInterval(pollingRef.current);
  }, [selectedPartner, fetchConversations]);

  const openChat = async (partner) => {
    setSelectedPartner(partner);
    try {
      const res = await getChatMessages(partner.id);
      setMessages(res.data);
      fetchConversations(); // Refresh to clear unread
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner) return;
    setSending(true);
    try {
      const res = await sendChatMessage({ receiver_id: selectedPartner.id, message: newMessage.trim() });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000 && d.getDate() === now.getDate()) {
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    if (diff < 172800000) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  // Users not in any conversation yet
  const existingPartnerIds = new Set(conversations.map(c => c.partner.id));
  const newUsers = allUsers.filter(u =>
    !existingPartnerIds.has(u.id) &&
    (searchUser === '' || u.name.toLowerCase().includes(searchUser.toLowerCase()))
  );

  const filteredConversations = conversations.filter(c =>
    searchUser === '' || c.partner.name.toLowerCase().includes(searchUser.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="chat-page">
      {/* Sidebar - Conversations */}
      <div className="w-80 border-r border-zinc-200 flex flex-col bg-white">
        <div className="p-4 border-b border-zinc-100">
          <h2 className="text-lg font-bold tracking-tight mb-3" style={{ fontFamily: 'Manrope' }}>Messages</h2>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              placeholder="Search people..."
              className="pl-9 rounded-sm h-9 text-sm"
              data-testid="chat-search-input"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : (
            <>
              {/* Existing Conversations */}
              {filteredConversations.map(conv => (
                <button
                  key={conv.partner.id}
                  onClick={() => openChat(conv.partner)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-zinc-50 transition-colors border-b border-zinc-50 text-left ${
                    selectedPartner?.id === conv.partner.id ? 'bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]' : ''
                  }`}
                  data-testid={`conversation-${conv.partner.id}`}
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarFallback className="bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold">
                      {getInitials(conv.partner.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm truncate">{conv.partner.name}</span>
                      <span className="text-[10px] text-zinc-400 shrink-0">{formatTime(conv.last_message_at)}</span>
                    </div>
                    <p className="text-xs text-zinc-500 truncate mt-0.5">
                      {conv.last_sender_id === user.id && <span className="text-zinc-400">You: </span>}
                      {conv.last_message}
                    </p>
                  </div>
                  {conv.unread_count > 0 && (
                    <Badge className="bg-[#002FA7] text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center rounded-full">
                      {conv.unread_count}
                    </Badge>
                  )}
                </button>
              ))}

              {/* New users to start chat with */}
              {searchUser && newUsers.length > 0 && (
                <>
                  <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-zinc-400 bg-zinc-50">Start new conversation</div>
                  {newUsers.slice(0, 10).map(u => (
                    <button
                      key={u.id}
                      onClick={() => openChat(u)}
                      className="w-full p-4 flex items-center gap-3 hover:bg-zinc-50 transition-colors border-b border-zinc-50 text-left"
                      data-testid={`new-chat-${u.id}`}
                    >
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback className="bg-zinc-100 text-zinc-600 text-xs font-semibold">
                          {getInitials(u.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <span className="font-medium text-sm">{u.name}</span>
                        <p className="text-xs text-zinc-500 capitalize">{u.roles?.length ? u.roles.map(r => r === 'account_manager' ? 'AM' : r === 'operations_manager' ? 'OM' : r).join(', ') : u.role}</p>
                      </div>
                    </button>
                  ))}
                </>
              )}

              {conversations.length === 0 && !searchUser && (
                <div className="p-8 text-center">
                  <ChatCircleDots size={40} className="mx-auto text-zinc-300 mb-3" />
                  <p className="text-sm text-zinc-500">No conversations yet</p>
                  <p className="text-xs text-zinc-400 mt-1">Search for a team member to start chatting</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-zinc-50">
        {selectedPartner ? (
          <>
            {/* Chat Header */}
            <div className="h-16 px-6 flex items-center gap-3 bg-white border-b border-zinc-200">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold">
                  {getInitials(selectedPartner.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-sm">{selectedPartner.name}</p>
                <p className="text-xs text-zinc-500 capitalize">{selectedPartner.roles?.length ? selectedPartner.roles.map(r => r === 'account_manager' ? 'AM' : r === 'operations_manager' ? 'OM' : r).join(', ') : selectedPartner.role || selectedPartner.email}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="chat-messages-area">
              {messages.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-zinc-400">Send a message to start the conversation</p>
                </div>
              )}
              {messages.map(msg => {
                const isMine = msg.sender_id === user.id;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
                    <div className={`max-w-[65%] px-4 py-2.5 rounded-2xl ${
                      isMine
                        ? 'bg-[#002FA7] text-white rounded-br-sm'
                        : 'bg-white border border-zinc-200 rounded-bl-sm'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                      <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
                        <span className={`text-[10px] ${isMine ? 'text-white/60' : 'text-zinc-400'}`}>
                          {formatTime(msg.created_at)}
                        </span>
                        {isMine && msg.is_read && (
                          <Circle size={6} weight="fill" className="text-white/60" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSend} className="p-4 bg-white border-t border-zinc-200">
              <div className="flex gap-3">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="rounded-full px-5"
                  data-testid="chat-message-input"
                  autoFocus
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-full h-10 w-10 p-0 shrink-0"
                  data-testid="chat-send-btn"
                >
                  <PaperPlaneRight size={18} />
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <ChatCircleDots size={64} className="mx-auto text-zinc-200 mb-4" />
              <p className="text-lg font-medium text-zinc-400">Select a conversation</p>
              <p className="text-sm text-zinc-400 mt-1">or search for a team member to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
